<template>
    <p class="search-results-found" v-if="searchResults?.total > 0">{{ searchResults.total }} room{{ searchResults.total !== 1 ? 's' : '' }} found.</p>
    <p v-else class="search-results-found search-results-found--no-results">No rooms found.</p>
    <SearchFilters
        :aggregations="aggregations"
        :search-params="searchParams"
        :location-names="locationNames"
        :university-names="universityNames"
        :search-mode="searchMode"
        :predefined-filters="predefinedFilters"
        :current-academic-year="currentAcademicYear"
        :next-academic-year="nextAcademicYear"
        @submit="searchSubmit"
        @changed-filter="changedFilter" />
    <template v-if="searchResults && searchParams">
        <SearchResultList
            v-if="mode == 'full' || mode == 'alternative'"
            :results="searchResults"
            :current-academic-year="currentAcademicYear"
            :next-academic-year="nextAcademicYear"
            :search-params="searchParams"
            :show-remaining="showRemaining"
            @set-page="setPage" />
    </template>
</template>
<script setup>
import { ref, onMounted, reactive } from 'vue';
import axios from 'axios';
import { apiUrl } from '@utils/api.js';

const searchModes = ['full', 'home', 'alternative'];

const props = defineProps({
    // The search params, initially passed in from the server
    searchParams: {
        type: Object,
        default: {}
    },
    // The current academic year range as a string, e.g. 2023/24
    currentAcademicYear: {
        type: String,
        required: true
    },
    // The next academic year range as a string, e.g. 2024/25
    nextAcademicYear: {
        type: String,
        required: true
    },
    locationNames: {
        type: Array,
        default: () => []
    },
    universityNames: {
        type: Array,
        default: () => []
    },
    mode: {
        type: String,
        default: 'full'
    },
    predefinedFilters: {
        type: Object,
        default: () => {}
    },
    excludeIds: {
        type: Array,
        default: () => []
    },
    showRemaining: {
        type: Boolean,
        default: true
    }
});

//#region Search
// The search mode - either 'full' or 'home'
const searchMode = ref('full');

// Hold our search results
const searchResults = ref(null);

// Submit the search form
const searchSubmit = async () => {
    if (searchMode.value == 'home') {
        // If we're on the home page, we want to redirect to the /search page
        // and add all of the search parameters
        const url = new URL('/search', window.location.href);
        Object.entries(props.searchParams).forEach(([key, value]) => {
            url.searchParams.set(key, value);
        });
        window.location.href = url;        
        return;
    }
    setPage(1);
}


let searchTimeOut;
// Fetch the search results from the API
const doSearch = () => {
    clearTimeout(searchTimeOut)
    searchTimeOut = setTimeout(async () => {

        let response;
        try {
            response = await axios.get(`${apiUrl}/search`, {
                params: {
                    ...props.searchParams,
                    exclude_ids: props.excludeIds.join(',')
                }
            });
        } catch (error) {
            console.error(error);
            return;
        }
        if (!response?.data || response.status < 200 || response.status >= 400) {
            console.error(response);
            return;
        }

        // Update the results object
        searchResults.value = response.data;

        // Update the aggregations
        await updateAggregations(response.data.aggregations);
    }, 1)    
    // Fetch the search results from the API
};

onMounted(() => {
    // Set the search mode
    if (searchModes.includes(props.mode)) {
        searchMode.value = props.mode;
    }

    if (searchMode.value == 'home') {
        // If we're on the home page, we don't need to do a search, so just update
        // the aggregations
        updateAggregations();
        return;
    }
    if (searchMode.value == 'alternative') {
        props.searchParams.per_page = 4;
        props.searchParams.show_available = true;
        props.searchParams.sort = "priceasc"
    }
    searchSubmit();
});

//#endregion

//#region Filters

// Our list of aggregations for filters - these lists are fetched from the API
// and used to populate the filter dropdowns, as well as show the number of
// possible results for each filter
const aggregations = reactive({
    filters: {
        locations: [],
        properties: [],
        room_types: [],
        universities: [],
    },
    updating: false
});

/**
 * Fetch the filter aggregations from the API and update the aggregations object
 */
const updateAggregations = async (aggs) => {
    aggregations.updating = true;
    let response = {
        data: aggs
    };
    if (!response?.data) {
        // Fetch the aggregations from the API
        try {
            response = await axios.get(`${apiUrl}/search-filters`, {
                params: props.searchParams
            });
        } catch (error) {
            console.error(error);
            return;
        }
    }
    if (!response?.data || response.status < 200 || response.status >= 400) {
        console.error(response);
        return;
    }

    // Update the aggregations object
    Object.keys(aggregations.filters).forEach((key) => {
        if (!response.data.hasOwnProperty(key)) {
            return;
        }
        // Aggregations come back as key/value pairs of the name and the count, for
        // example: { "London": 10, "Manchester": 5 } so we need to map them to an
        // array of objects with the name and count properties
        aggregations.filters[key] = Object.entries(response.data[key]).map(([key, value]) => {
            return {
                name: key,
                count: value
            }
        });
    });
    aggregations.updating = false;
}

/**
 * Handle changed-filter events from the SearchFilters component
 * @param {Object} filter the filter as an object containing the `name` and `value`
 */
const changedFilter = (filter) => {
    const aggregationsToChange = [
        'filter_location',
        'filter_university',
        'filter_property',
        'filter_room_type',
        'academic_year_offset',
        'show_available'
    ];
    if (aggregationsToChange.includes(filter.name)) {
        // If the filter is one of the aggregations, update the aggregations
        updateAggregations();
    }
}

/**
 * Update the URL with the current search params so searches can be shared or
 * the page refreshed without losing the search params
 */
const updateURL = () => {
    const url = new URL(window.location.href);

    // Fetch all of the search params and set them as URL params
    Object.entries(props.searchParams).forEach(([key, value]) => {

        if (key === "exclude_ids") {
            url.searchParams.delete(key);
            return;
        }

        const predefinedKeys = Object.keys(props.predefinedFilters);
        if (predefinedKeys.includes(key) || predefinedKeys.includes(key.replace('filter_', ''))) {
            // If the value is the same as the predefined filter, remove it from the URL
            url.searchParams.delete(key);
            return;
        }

        // Convert booleans to 1 or 0
        if (typeof value === 'boolean') {
            value = Boolean(value) ? 1 : 0;
        }
        if (!value) {
            // If the value is empty, remove it from the URL
            url.searchParams.delete(key);
            return;
        }
        url.searchParams.set(key, value);
    });

    // Replace the current URL with the new one without reloading the page
    window.history.replaceState({}, '', url);
}

//#endregion

//#region Pagination

const setPage = (value) => {
    props.searchParams.current_page = value;
    updateURL();
    doSearch();
};

//#endregion

</script>